import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { OrganisationDataService, IOrganisation, Organisation, OrganisationsHierarchy, OrganisationDataModelNode } from '@services/organisation-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize, tap, withLatestFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { DatapointEnergyMetric, DatapointReportItem, EnergyReportRequest, ReportPDFService, SummaryReport, TotalisedEnergyMetric, TotalisedReportItem } from '@services/report-data.service';
import { ChartComponent, FontModel, AccumulationChartComponent } from '@syncfusion/ej2-angular-charts';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { MatDialog } from '@angular/material/dialog';
import { CreateOrganisationReportComponent, CreateOrganisationReportData } from 'src/app/dialogs/create-organisation-report/create-organisation-report.component';

@Component({
	selector: 'app-energy-reporting',
	templateUrl: './energy-reporting.component.html',
	styleUrls: ['./energy-reporting.component.scss']
})
export class EnergyReportingComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** energyAssets */
	public energyAssets: { Asset: LevelAsset, Datapoints: AssetDataPoint[] }[] = [];

	/** datapointAssets */
	public datapointAssets: { Datapoint: AssetDataPoint, Asset: LevelAsset }[] = [];

	/** datapointAssets */
	public filteredDatapointAssets: { Datapoint: AssetDataPoint, Asset: LevelAsset }[] = [];

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** startDate */
	public startDate: Date;

	/** endDate */
	public endDate: Date;

	/** recentRunningValue */
	public recentRunningValue: number = 0;

	/** now */
	public now: Date = new Date();

	/** numDays */
	public numDays: number = 1;

	/** totalDifference */
	public totalDifference: number = 0;

	/** totalAvgRunning */
	public totalAvgRunning: number = 0;

	/** totalAvgPeriod */
	public totalAvgPeriod: number = 0;

	/** totalDeltaData */
	public totalDeltaData: ISensorData[] = [];

	/** totalCumulativeData */
	public totalCumulativeData: ISensorData[] = [];

	/** totalMax */
	public totalMax: number = 0;

	/** totalMin */
	public totalMin: number = 0;

	/** deltaMax */
	public deltaMax: number = 0;

	/** deltaMin */
	public deltaMin: number = 0;

	/** targetSI */
	public targetSI: string = "kWh";

	/** strDuration */
	public strDuration: string = "01:00:00";

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** DAY */
	public DAY: number = (1 * 24 * 60 * 60 * 1000);

	/** regex */
	public regex = /^\[[0-9]*\]$/;

	/** regexDelta */
	public regexDelta = /^\[[0-9]*_DELTA\]$/;

	/** regexCalc */
	public regexCalc = /^\[[0-9]*_DELTA_CALCULATED\]$/;

	/** regexDeltaTime */
	public regexDeltaTime = /^\[[0-9]*_DELTA_TIME\]$/;

	/** assetRelevantData */
	public assetRelevantData: { AssetID: number, RelevantData: ISensorData[], RelevantDeltaData: ISensorData[] }[] = [];

	/** datapointRelevantData */
	public datapointRelevantData: { DatapointID: number, SI: string, RelevantData: ISensorData[], RelevantDeltaData: ISensorData[], DPTitle: string }[] = [];

	/** assetEnergyMetrics */
	public assetEnergyMetrics: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number }[] = [];

	/** datapointEnergyMetrics */
	public datapointEnergyMetrics: DatapointEnergyMetric[] = [];

	/** includedDatapointAssets */
	public includedDatapointAssets: { Datapoint: AssetDataPoint, Asset: LevelAsset }[] = [];

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	/** selectedLocation */
	public selectedLocation?: OrganisationsHierarchy;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** exportTypes */
	public exportTypes: { text: string, value: number }[] =
		[
			{
				text: "PDF",
				value: 0
			},
			{
				text: "Excel",
				value: 1
			}
		];

	/** exportFields */
	public exportFields: Object = { text: "text", value: "value" };

	/** detailTypes */
	public detailTypes: { text: string, value: number }[] =
		[
			{
				text: "Simple",
				value: 0
			},
			{
				text: "Detailed",
				value: 1
			}
		];

	/** detailFields */
	public detailFields: Object = { text: "text", value: "value" };

	/** selectedExportType */
	public selectedExportType: number = 0; //PDF by default

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** Chart Legend */
	public legend: Object;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Tooltip Settings */
	public tooltipSettings: Object;

	/** Chart boarder Settings */
	public borderSettings: Object;

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Sensor X Axis */
	public sensorXAxis: Object;

	/** titleStyle */
	public titleStyle: FontModel = {
		fontWeight: "bold",
		size: "large"
	}

	/** sumDeltaChart */
	@ViewChild('sumDeltaChart') sumDeltaChart!: ChartComponent;

	/** sumDeltaChart */
	@ViewChild('sumTotalChart') sumTotalChart!: ChartComponent;

	/** charts */
	@ViewChildren(ChartComponent) charts!: QueryList<ChartComponent>;

	/** pieCharts */
	@ViewChildren(AccumulationChartComponent) pieCharts!: QueryList<AccumulationChartComponent>;

	/** deltaSeriesColour */
	public deltaSeriesColour: string = "lightseagreen";

	/** selectedDetailOption */
	public selectedDetailOption: number = 0;

	/** runningThreshold */
	public runningThreshold: number = 0.05;

	/** useNewLayout */
	public useNewLayout: boolean = true;

	/** includedNodes */
	public includedNodes: OrganisationDataModelNode[] = [];

	/** dataModelNodeFields */
	public dataModelNodeFields: Object = { text: "Name", id: "DataModelNodeID" };

	/** reportFields */
	public reportFields: Object = { text: "Name", id: "OrganisationReportID" };

	/** listViewInstance */
	@ViewChild('listviewInstance') listViewInstance!: ListViewComponent;

	/** reportDatapointEnergyMetrics */
	public reportDatapointEnergyMetrics: DatapointEnergyMetric[] = [];

	/** organisationDataModel */
	public organisationDataModel: OrganisationDataModelNode[] = [];

	/** flattenedDataModel */
	public flattenedDataModel: OrganisationDataModelNode[] = [];

	/** summaryReports */
	public summaryReports: SummaryReport[] = [];

	/** reportTotalisedEnergyMetrics */
	public reportTotalisedEnergyMetrics: TotalisedEnergyMetric[] = [];

	/** pieChartData */
	public pieChartData: { title: string, nodeUID: string, name: string, usage: number }[][] = [];

	/** pieChartLabels */
	public pieChartLabels: Object = {
		visible: true,
		position: 'Outside', name: 'name',
		// font: {
		//     size: Browser.isDevice ? '8px' : '12px',
		//     fontWeight: '600'
		// },
		connectorStyle: { length: '40px', type: 'Curve' },
		maxWidth: 200
	};

	/** pieChartLegendSettings */
	public pieChartLegendSettings: Object = {
		visible: true,
		// maximumLabelWidth: 500
		width: "400",
		// position: "Bottom"
	}

	/** waiting */
	public waiting: boolean = false;

	/**
	 * Default Constructor
	 * @param auth auth 
	 * @param organisationDataService organisationDataService 
	 * @param globalFunctions globalFunctions 
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param assetsDataService assetsDataService 
	 * @param document document 
	 */
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		private reportPDFService: ReportPDFService,
		@Inject(DOCUMENT) public document: Document,
		public dialog: MatDialog
	) {
		/** Get required subscriptions */
		combineLatest([
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			withLatestFrom(
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			),
			// Ensure that subscriptions are not null or undefined
			filter(([[paramMap, hierarchy], selectedAccount, selectedOrganisation, currentUser]) =>
				!!paramMap && !!currentUser && !!hierarchy && !!selectedAccount && !!selectedOrganisation)
		).subscribe({
			next: ([[paramMap, hierarchy], selectedAccount, selectedOrganisation, currentUser]) => {
				this.thisUser = currentUser;
				this.selectedAccount = <IAccount>selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.organisation = <IOrganisation>selectedOrganisation;

				this.accountGUID = this.selectedAccount.GUID;
				this.organisationUID = this.organisation.Uid;

				this.hierarchy = JSON.parse(hierarchy);

				this.endDate = new Date();
				this.startDate = new Date();
				this.startDate.setDate(this.endDate.getDate() - this.numDays);

				this.MainLocations = [];

				if (this.hierarchy[0].SubChild) {
					this.hierarchy[0].SubChild.forEach(subchild => {
						this.MainLocations.push(subchild);
					});
				}

				this.legend = { visible: true };

				this.getEnergyAssets();
				// this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());

				this.getSummaryReports();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
			this.globalFunctions.displaySpinner(false);
		}
		catch { }
	}


	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.setXAxis();
	}

	/**
	 * getEnergyAssets
	 */
	getEnergyAssets() {
		this.datapointAssets = [];

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getEnergyAssets$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (assets) => {
				this.energyAssets = assets
					.map((x: { Asset: string, Datapoints: string }) => ({
						Asset: JSON.parse(x.Asset),
						Datapoints: JSON.parse(x.Datapoints)
					}));

				this.energyAssets.forEach(energyAsset => {
					energyAsset.Datapoints.forEach(datapoint => {
						this.datapointAssets.push({ Datapoint: datapoint, Asset: energyAsset.Asset });
					});
				});

				this.filteredDatapointAssets = this.datapointAssets;
				this.includedDatapointAssets = this.datapointAssets;

				this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.getEnergyAssets$:", error);
				}
			},
		});
	}

	/**
	 * Get Values for the DataPoints
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetEnergyData(dateStart: any, dateEnd: any, noSpinner: boolean = false): Promise<boolean> {
		if (!noSpinner) {
			this.globalFunctions.displaySpinner(true);
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.waiting = true;

		return new Promise<boolean>((resolve, reject) => {
			this.organisationDataService.getOrganisationEnergyData$(this.accountGUID, this.organisationUID, dateStart, dateEnd).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false);
					setTimeout(() => {
						this.waiting = false;
					}, 100);
				})
			).subscribe({
				next: (dataPointValues: any) => {
					try {
						let parsedData: ISensorData[]
						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								// AssetId: 0,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});
						console.log("Parsed DataPoints");

						parsedData.forEach((x: ISensorData) => {
							const DPID = parseInt(Object.entries(x).find((y) => this.regex.test(y[0]))?.[0].replace("[", "").replace("]", "") ?? "");
							if (DPID) {
								if (this.datapointAssets.map((x) => x.Datapoint).some((dp: AssetDataPoint) => dp.IsCumulative && dp.SI == "kWh" && dp.DataPointID == DPID)) {
									x[`[${DPID}_DELTA_CALCULATED]`] = (<number>this.getUsageValue(x, DPID)) ?? 0;
								}
							}
						});

						if (parsedData.length == 0) {
							this.globalFunctions.DisplaySnackBar({
								message: "No Data found for selected period",
								duration: 2000,
								action: ""
							});
							return;
						}

						this.parsedData = parsedData;

						this.totalDeltaData = [];
						this.totalCumulativeData = [];
						this.assetRelevantData = [];
						this.assetEnergyMetrics = [];
						this.recentRunningValue = 0;
						this.datapointRelevantData = [];
						this.pieChartData = [];
						this.datapointEnergyMetrics = [];

						// Getting total difference, max and min
						let totalStart: number = 0;
						let totalEnd: number = 0;
						let seenDPsStart: string[] = [];
						let seenDPsEnd: string[] = [];
						for (let i: number = 0; i < this.datapointAssets.length * 4; i++) { // make sure we loop enough times to see each datapoint at least once
							Object.entries(this.parsedData[i]).forEach(item => {
								if (this.regex.test(item[0]) && !seenDPsStart.includes(item[0])) {
									seenDPsStart.push(item[0]);
									totalStart += <number>item[1];
								}
							});
							Object.entries(this.parsedData[this.parsedData.length - (i + 1)]).forEach(item => {
								if (this.regex.test(item[0]) && !seenDPsEnd.includes(item[0])) {
									seenDPsEnd.push(item[0]);
									totalEnd += <number>item[1];
								}
							});
						}
						this.totalDifference = parseFloat((totalEnd - totalStart).toFixed(3));
						this.totalMax = parseFloat(totalEnd.toFixed(3));
						this.totalMin = parseFloat(totalStart.toFixed(3));

						// Getting Period and Running Averages, delta max and min
						let numTotal: number = 0;
						let numRunning: number = 0;
						this.parsedData.forEach(x => {
							Object.entries(x).forEach(item => {
								// if (this.regexDelta.test(item[0])) {
								if (this.regexCalc.test(item[0])) {
									const value = <number>item[1];
									this.totalAvgPeriod += value;
									numTotal++;
									if (value > this.runningThreshold) {
										this.totalAvgRunning += value;
										numRunning++;
									}
									if (value < this.deltaMin) {
										this.deltaMin = value;
									}
									if (value > this.deltaMax) {
										this.deltaMax = value;
									}
								}
							});
						});
						this.totalAvgPeriod = parseFloat((this.totalAvgPeriod / numTotal).toFixed(3));
						this.totalAvgRunning = parseFloat((this.totalAvgRunning / numRunning).toFixed(3));

						const FIVE_MINUTES = (1000 * 60 * 5);

						// Getting total Delta Values 
						for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
							const end = start + FIVE_MINUTES;
							let data = this.parsedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
							let avgValues: [string, number, number][] = [];
							data.forEach((y: ISensorData) => {
								Object.entries(y).forEach(item => {
									// if (this.regexDelta.test(item[0])) {
									if (this.regexCalc.test(item[0])) {
										let existingItem = avgValues.find(av => av[0] == item[0]);
										if (existingItem) {
											existingItem[1] += <number>item[1];
											existingItem[2]++;
										} else {
											avgValues.push([item[0], <number>item[1], 1]);
										}
									}
								});
							});
							let timestamp = end - ((end - start) / 2);
							let total: number = 0;
							avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
							if (data.length > 0) {
								this.totalDeltaData.push({
									timestamp: timestamp,
									sampleDate: new Date(timestamp).toString(),
									...{ "Total_DELTA": total }
								});
							}
						}

						// Getting total Cumulative Values 
						for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
							const end = start + FIVE_MINUTES;
							let data = this.parsedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
							let avgValues: [string, number, number][] = [];
							data.forEach((y: ISensorData) => {
								Object.entries(y).forEach(item => {
									if (this.regex.test(item[0])) {
										let existingItem = avgValues.find(av => av[0] == item[0]);
										if (existingItem) {
											existingItem[1] += <number>item[1];
											existingItem[2]++;
										} else {
											avgValues.push([item[0], <number>item[1], 1]);
										}
									}
								});
							});
							let timestamp = end - ((end - start) / 2);
							let total: number = 0;
							avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
							let isAbove: boolean = this.totalCumulativeData.length > 0 ?
								((total ?? 0) > <number>this.totalCumulativeData[this.totalCumulativeData.length - 1]["Total"]) : true;
							if (data.length > 0 && isAbove) {
								this.totalCumulativeData.push({
									timestamp: timestamp,
									sampleDate: new Date(timestamp).toString(),
									...{ "Total": total }
								});
							}
						}

						// Getting recent running value
						let seenDPsRecent: string[] = [];
						for (let i: number = 0; i < 10; i++) {
							Object.entries(this.parsedData[this.parsedData.length - (i + 1)]).forEach(item => {
								// if (this.regexDelta.test(item[0]) && !seenDPsRecent.includes(item[0])) {
								if (this.regexCalc.test(item[0]) && !seenDPsRecent.includes(item[0])) {
									seenDPsRecent.push(item[0]);
									this.recentRunningValue += <number>item[1];
								}
							});
						}
						this.recentRunningValue = parseFloat(this.recentRunningValue.toFixed(3));

						// if (this.energyAssets.length > 0) {
						// 	this.energyAssets.forEach((x) => this.getAssetRelevantData(x));
						// }
						if (this.datapointAssets.length > 0) {
							this.datapointAssets.map((x) => x.Datapoint).forEach((y) => this.getDatapointRelevantData(y));
						}

						// if (this.assetRelevantData.length > 0) {
						// 	this.assetRelevantData.forEach((x) => this.assetEnergyMetrics.push(this.calculateAsssetEnergyMetrics(x.AssetID)));
						// }
						if (this.datapointRelevantData.length > 0) {
							this.datapointRelevantData.forEach((x) => this.datapointEnergyMetrics.push(this.calculateDatapointEnergyMetrics(x)));
						}

						if (this.datapointEnergyMetrics.length > 0) {
							this.setPieChartData();
						}

						// Trend the total delta values and total cumulative values		
						if (this.sumDeltaChart) {
							this.sumDeltaChart.clearSeries();
						}
						if (this.sumTotalChart) {
							this.sumTotalChart.clearSeries();
						}

						this.trendDeltaValues();
						this.trendTotalValues();

						setTimeout(() => {
							this.plotRelevantDPData();
						}, 100);

						resolve(true);
					} catch (error) {
						console.error(error);
						reject(true);
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.organisationDataService.getOrganisationEnergyData$", false);
					}
					reject(true);
				}
			});
		});
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param energyAsset energyAsset 
	 * @returns 
	 */
	getAssetRelevantData(energyAsset: { Asset: LevelAsset, Datapoints: AssetDataPoint[] }) {
		if (this.parsedData.length > 0) {
			let relevantData: ISensorData[] = [];
			let relevantDeltaData: ISensorData[] = [];
			this.parsedData.map((x: ISensorData) => {
				Object.entries(x).forEach(item => {
					const dp = energyAsset.Datapoints.find((y: AssetDataPoint) => y.DataPointID == item[0]);
					let iskWh: boolean = false;
					if (dp && dp.SI == "kWh") iskWh = true;
					if (this.regex.test(item[0]) && energyAsset.Datapoints.map((x: DataPoint) => `[${x.DataPointID}]`).includes(item[0])) {
						relevantData.push({
							timestamp: x.timestamp,
							sampleDate: x.sampleDate.toString(),
							[item[0]]: item[1]
						});
					}
					else if (!iskWh && this.regexDelta.test(item[0]) && energyAsset.Datapoints.map((x: DataPoint) => `[${x.DataPointID}_DELTA]`).includes(item[0])) {
						relevantDeltaData.push({
							timestamp: x.timestamp,
							sampleDate: x.sampleDate.toString(),
							[item[0]]: item[1]
						});
					}
					else if (iskWh && this.regexCalc.test(item[0]) && energyAsset.Datapoints.map((x: DataPoint) => `[${x.DataPointID}_DELTA_CALCULATED]`).includes(item[0])) {
						relevantDeltaData.push({
							timestamp: x.timestamp,
							sampleDate: x.sampleDate.toString(),
							[item[0]]: item[1]
						});
					}
				});
			});
			this.assetRelevantData.push({ AssetID: energyAsset.Asset.AssetID, RelevantData: relevantData, RelevantDeltaData: relevantDeltaData });
		}
	}

	/**
	 * getDatapointRelevantData
	 * @param datapoint datapoint 
	 */
	getDatapointRelevantData(datapoint: AssetDataPoint) {
		if (this.parsedData.length > 0) {
			let relevantData: ISensorData[] = [];
			let relevantDeltaData: ISensorData[] = [];
			this.parsedData.forEach((x: ISensorData) => {
				const deltaTime = Object.entries(x).find((x) => this.regexDeltaTime.test(x[0]))?.[1];
				if (deltaTime) {
					Object.entries(x).forEach(item => {
						if (this.regex.test(item[0]) && `[${datapoint.DataPointID}]` == item[0]) {
							relevantData.push({
								timestamp: x.timestamp,
								sampleDate: x.sampleDate.toString(),
								[item[0]]: item[1]
							});
						}
						else if (datapoint.SI != "kWh" && this.regexDelta.test(item[0]) && `[${datapoint.DataPointID}_DELTA]` == item[0]) {
							relevantDeltaData.push({
								timestamp: x.timestamp,
								sampleDate: x.sampleDate.toString(),
								[item[0]]: item[1],
								DELTA_TIME: deltaTime
							});
						}
						else if (datapoint.SI == "kWh" && this.regexCalc.test(item[0]) && `[${datapoint.DataPointID}_DELTA_CALCULATED]` == item[0]) {
							relevantDeltaData.push({
								timestamp: x.timestamp,
								sampleDate: x.sampleDate.toString(),
								[item[0]]: item[1],
								DELTA_TIME: deltaTime
							});
						}
					});
				}
			});
			this.datapointRelevantData.push({ DatapointID: datapoint.DataPointID, SI: datapoint.SI, DPTitle: datapoint.DPTitle, RelevantData: relevantData, RelevantDeltaData: relevantDeltaData });
		}
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	calculateAsssetEnergyMetrics(assetID: number): { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } {
		let result: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } = {
			AssetID: assetID,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0
		}
		if (this.assetRelevantData.length > 0) {
			let data = this.assetRelevantData.find((x) => x.AssetID === assetID);
			if (data) {
				let totalCurrent: number = 0;
				let totalAvg: number = 0;
				let numValues: number = 0;
				let totalRunningAvg: number = 0;
				let numRunningValues: number = 0;
				let totalTotal: number = 0;
				let totalDiff: number = 0;
				let totalEnd: number = 0;
				let totalStart: number = 0;
				for (let i: number = 0; i < data.RelevantDeltaData.length; i++) {
					Object.entries(data.RelevantDeltaData[i]).forEach(item => {
						if (i == (data!.RelevantDeltaData.length - 1)) {
							// if (this.regexDelta.test(item[0])) {
							if (this.regexCalc.test(item[0])) {
								totalCurrent += <number>item[1];
							}
						}
						// if (this.regexDelta.test(item[0])) {
						if (this.regexCalc.test(item[0])) {
							const value = <number>item[1];
							numValues++;
							totalAvg += value;
							if (value > this.runningThreshold) {
								numRunningValues++;
								totalRunningAvg += value;
							}
						}
					});
				}
				totalCurrent = parseFloat(totalCurrent.toFixed(2));
				totalAvg = parseFloat((totalAvg / (numValues || 1)).toFixed(2));
				totalRunningAvg = parseFloat((totalRunningAvg / (numRunningValues || 1)).toFixed(2));
				Object.entries(data.RelevantData[data.RelevantData.length - 1]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalTotal += value;
						totalEnd += value;
					}
				});
				Object.entries(data.RelevantData[0]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalStart += value;
					}
				});
				totalTotal = parseFloat(totalTotal.toFixed(2));
				totalDiff = parseFloat((totalEnd - totalStart).toFixed(2));
				result.Current = totalCurrent;
				result.Avg = totalAvg;
				result.RunningAvg = totalRunningAvg;
				result.Total = totalTotal;
				result.Diff = totalDiff;
			}
		}
		return result;
	}

	/**
	 * calculateDatapointEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	calculateDatapointEnergyMetrics(data: { DatapointID: number, SI: string, RelevantData: ISensorData[], RelevantDeltaData: ISensorData[] }): DatapointEnergyMetric {
		const iskWh: boolean = data.SI == "kWh";
		let result: DatapointEnergyMetric = {
			DatapointID: data.DatapointID,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0,
			Min: 0,
			Max: 0,
			RunTime: 0
		}
		if (this.datapointRelevantData.length > 0) {
			if (data && data.RelevantData.length > 0 && data.RelevantDeltaData.length > 0) {
				let totalCurrent: number = 0;
				let totalAvg: number = 0;
				let numValues: number = 0;
				let totalRunningAvg: number = 0;
				let numRunningValues: number = 0;
				let totalTotal: number = 0;
				let totalDiff: number = 0;
				let totalEnd: number = 0;
				let totalStart: number = 0;
				let min: number = Infinity;
				let max: number = 0;
				let runTime: number = 0;
				for (let i: number = 0; i < data.RelevantDeltaData.length; i++) {
					if (!iskWh && <number>Object.entries(data.RelevantDeltaData[i]).find((x) => this.regexDelta.test(x[0]))?.[1] > this.runningThreshold) {
						const timeDiff = <number>Object.entries(data.RelevantDeltaData[i]).find((x) => x[0] == "DELTA_TIME")?.[1];
						if (timeDiff) {
							runTime += timeDiff;
						}
					}
					if (iskWh && <number>Object.entries(data.RelevantDeltaData[i]).find((x) => this.regexCalc.test(x[0]))?.[1] > this.runningThreshold) {
						const timeDiff = <number>Object.entries(data.RelevantDeltaData[i]).find((x) => x[0] == "DELTA_TIME")?.[1];
						if (timeDiff) {
							runTime += timeDiff;
						}
					}
					Object.entries(data.RelevantDeltaData[i]).forEach(item => {
						if (i == (data!.RelevantDeltaData.length - 1)) {
							if (!iskWh && this.regexDelta.test(item[0])) {
								totalCurrent += <number>item[1];
							}
							if (iskWh && this.regexCalc.test(item[0])) {
								totalCurrent += <number>item[1];
							}
						}
						if (!iskWh && this.regexDelta.test(item[0])) {
							const value = <number>item[1];
							numValues++;
							totalAvg += value;
							if (value > this.runningThreshold) {
								numRunningValues++;
								totalRunningAvg += value;
							}
							if (value > max) {
								max = value;
							};
							if (value < min) {
								min = value;
							};
						}
						if (iskWh && this.regexCalc.test(item[0])) {
							const value = <number>item[1];
							numValues++;
							totalAvg += value;
							if (value > this.runningThreshold) {
								numRunningValues++;
								totalRunningAvg += value;
							}
							if (value > max) {
								max = value;
							};
							if (value < min) {
								min = value;
							};
						}
					});
				}
				totalCurrent = parseFloat(totalCurrent.toFixed(2));
				totalAvg = parseFloat((totalAvg / (numValues || 1)).toFixed(2));
				totalRunningAvg = parseFloat((totalRunningAvg / (numRunningValues || 1)).toFixed(2));
				Object.entries(data.RelevantData[data.RelevantData.length - 1]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalTotal += value;
						totalEnd += value;
					}
				});
				Object.entries(data.RelevantData[0]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalStart += value;
					}
				});
				totalTotal = parseFloat(totalTotal.toFixed(2));
				totalDiff = parseFloat((totalEnd - totalStart).toFixed(2));
				if (min == Infinity) min = 0;
				min = parseFloat(min.toFixed(2));
				max = parseFloat(max.toFixed(2));
				result.Current = totalCurrent;
				result.Avg = totalAvg;
				result.RunningAvg = totalRunningAvg;
				result.Total = totalTotal;
				result.Diff = totalDiff;
				result.Min = min;
				result.Max = max;
				result.RunTime = runTime;
			}
		}
		return result;
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	getAsssetEnergyMetrics(assetID: number): { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } {
		let result: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } = {
			AssetID: 0,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0
		}
		if (this.assetEnergyMetrics.length > 0) {
			let _result = this.assetEnergyMetrics.find((x) => x.AssetID === assetID);
			if (_result) {
				result = _result;
			}
		}
		return result;
	}

	/**
	 * getDatapointEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	getDatapointEnergyMetrics(datapointID: number): DatapointEnergyMetric {
		let result: DatapointEnergyMetric = {
			DatapointID: 0,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0,
			Min: 0,
			Max: 0,
			RunTime: 0
		}
		if (this.datapointEnergyMetrics.length > 0) {
			let _result = this.datapointEnergyMetrics.find((x) => x.DatapointID === datapointID);
			if (_result) {
				result = _result;
			}
		}
		return result;
	}

	/**
	 * convertEpoch
	 * @param epoch epoch 
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * DatePickerChange
	 * @param event event 
	 */
	DatePickerChange(event: any) {
		try {
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1)

					/** Reset Duration Search */
					this.strDuration = "custom";

					this.numDays = parseFloat(((this.endDate.getTime() - this.startDate.getTime()) / this.DAY).toFixed(2));

					this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * selectNumDays
	 * @param event event 
	 */
	selectNumDays(sDuration: string) {
		this.strDuration = sDuration

		let parts: any[] = this.strDuration.split(":")
		this.endDate = new Date()
		this.startDate = new Date()
		this.startDate.setDate(this.startDate.getDate() - parts[0]);
		this.startDate.setHours(this.startDate.getHours() - parts[1]);
		this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

		this.numDays = parseInt(parts[0]) || parseFloat((parseInt(parts[1]) / 24).toFixed(2));

		this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
	}

	/**
	 * checkDatapointIncluded
	 * @param datapoint datapoint 
	 * @returns 
	 */
	checkDatapointIncluded(datapoint: AssetDataPoint): boolean {
		let result: boolean = false;
		if (this.includedDatapointAssets.length > 0) {
			result = this.includedDatapointAssets.some((x) => x.Datapoint.DataPointID === datapoint.DataPointID);
		}
		return result;
	}

	/**
	 * excludeIncludeDatapoints 
	 * @param include include 
	 */
	excludeIncludeDatapoints(include: boolean) {
		this.includedDatapointAssets = include ? this.datapointAssets : [];
	}

	/**
	 * toggleDatapointInclusion
	 * @param event event 
	 * @param datapointAsset datapointAsset  
	 */
	toggleDatapointInclusion(event: any, datapointAsset: { Datapoint: AssetDataPoint, Asset: LevelAsset }) {
		if (event.checked) {
			this.includedDatapointAssets.push(datapointAsset);
		} else {
			this.includedDatapointAssets = this.includedDatapointAssets.filter((x) => x.Datapoint.DataPointID != datapointAsset.Datapoint.DataPointID);
		}
	}



	/** 
	 * filterByLocation
	 */
	filterByLocation() {
		if (this.selectedLocation) {
			let list: LevelAsset[] = this.flattenAssets([this.selectedLocation]);
			this.filteredDatapointAssets = this.datapointAssets.filter((x) => list.map((y: LevelAsset) => y.AssetID).includes(x.Asset.AssetID));
		} else {
			this.filteredDatapointAssets = this.datapointAssets;
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * generateReport
	 */
	async generateReport() {
		let datapointReportItems: DatapointReportItem[] = [];
		let totalisedReportItems: TotalisedReportItem[] = [];
		this.reportDatapointEnergyMetrics.forEach((x: DatapointEnergyMetric) => {
			let findItem = this.includedDatapointAssets.find((y) => y.Datapoint.DataPointID == x.DatapointID);
			if (findItem) {
				let assetName: string = findItem.Asset.Name + " - " + findItem.Asset.Description;
				datapointReportItems.push({
					DatapointEnergyMetric: x,
					AssetName: assetName,
					DPTitle: findItem.Datapoint.DPTitle,
					ParentSensorID: findItem.Datapoint.SensorID
				});
			}
		});
		if (this.reportTotalisedEnergyMetrics.length > 0) {
			this.reportTotalisedEnergyMetrics.forEach((x: TotalisedEnergyMetric) => {
				totalisedReportItems.push({
					TotalisedEnergyMetric: x,
					NodeName: x.Name,
					NodeUID: x.NodeUID ?? "",
					ParentUID: x.ParentUID ?? "",
					Units: x.Units ?? ""
				});
			});
		}

		let deltaBase64: string = ""
		let totalBase64: string = "";
		if (this.sumDeltaChart) {
			deltaBase64 = this.convertChartToBase64(this.sumDeltaChart);
		}
		if (this.sumTotalChart) {
			totalBase64 = this.convertChartToBase64(this.sumTotalChart);
		}
		let dpCharts = this.convertAllDatapointChartsToBase64();
		const reportDpIDs = this.reportDatapointEnergyMetrics.map((x: DatapointEnergyMetric) => x.DatapointID);
		let reportDPCharts: { datapointID: any; dptitle: string; base64: string; }[] = [];
		reportDpIDs.forEach((id: number) => {
			const _chart = dpCharts.find((chart) => chart.datapointID == id);
			if (_chart) {
				reportDPCharts.push(_chart)
			}
		});
		let pieCharts = await this.convertAllPieChartsToBase64();
		const reportNodeUIDs = this.reportTotalisedEnergyMetrics.map((x: TotalisedEnergyMetric) => x.NodeUID);
		pieCharts = pieCharts.filter((chart) => reportNodeUIDs.includes(chart.nodeUID));

		if (this.selectedExportType == 0) {
			if (this.selectedDetailOption == 0) {
				this.generateSimplePDFReport(datapointReportItems, deltaBase64, totalBase64, reportDPCharts, totalisedReportItems, pieCharts);
			} else if (this.selectedDetailOption == 1) {
				this.generatePDFReport(datapointReportItems, deltaBase64, totalBase64, reportDPCharts, totalisedReportItems, pieCharts);
			}
		} else {
			if (this.selectedDetailOption == 0) {
				this.generateSimpleExcelReport(datapointReportItems, deltaBase64, totalBase64, reportDPCharts, totalisedReportItems, pieCharts);
			} else {
				this.generateExcelReport(datapointReportItems, deltaBase64, totalBase64, reportDPCharts, totalisedReportItems, pieCharts);
			}
		}
	}

	/**
	 * generateReport
	 */
	generatePDFReport(datapointReportItems: DatapointReportItem[],
		deltaBase64: string,
		totalBase64: string,
		dpCharts: { datapointID: any; dptitle: string; base64: string }[],
		totalisedReportItems?: TotalisedReportItem[],
		pieCharts?: { nodeUID: string, name: string; base64: string }[]) {
		this.now = new Date();

		let inputData: EnergyReportRequest = {
			AccountGUID: this.accountGUID,
			OrganisationUID: this.organisationUID,
			OrganisationName: this.organisation.Name,
			ReportTitle: `${this.organisation.Name} Utility Report ${this.formatDate(this.now)}`,
			PeriodStartPoint: this.formatDate(this.startDate),
			PeriodEndPoint: this.formatDate(this.endDate),
			PeriodLength: this.getPeriodLength(this.numDays),
			IncludedLocations: this.selectedLocation ? this.selectedLocation.Name : "All Locations",
			TargetSI: this.targetSI,
			SiteCurrent: this.recentRunningValue,
			SitePeriodAvg: this.totalAvgPeriod,
			SiteRunningAvg: this.totalAvgRunning,
			SiteDifference: this.totalDifference,
			SiteTotal: this.totalMax,
			DatapointReportItems: datapointReportItems,
			SiteDeltaChartBase64: deltaBase64,
			SiteTotalChartBase64: totalBase64,
			DatapointGraphBase64s: dpCharts,
			TotalisedReportItems: totalisedReportItems,
			TotalisedBase64s: pieCharts,
			IncludedNodes: this.includedNodes.map((node: OrganisationDataModelNode) => node.DataModelNodeUID)
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.generateEnergyReportPDF$(this.accountGUID, this.organisationUID, inputData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				// Determine the MIME type and handle accordingly
				const mimeType = response.type || 'application/pdf'; // Default to PDF if type is not provided
				let fileName = `${this.organisation.Name.replaceAll(" ", "_")}_Utility_Report_${this.formatDate(this.now, true)}`;

				if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					fileName += '.xlsx';
				} else if (mimeType === 'application/pdf') {
					fileName += '.pdf';
				} else {
					fileName += '.txt'; // Default to txt for unknown types
				}

				let anchor = document.createElement('a');
				const blob = new Blob([response], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				anchor.download = fileName;
				anchor.href = url;
				anchor.click();

				window.URL.revokeObjectURL(url);
				anchor.remove();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.generateEnergyReportPDF$:", error);
				}
			},
		});
	}

	/**
	 * generateReport
	 */
	generateSimplePDFReport(datapointReportItems: DatapointReportItem[],
		deltaBase64: string,
		totalBase64: string,
		dpCharts: { datapointID: any; dptitle: string; base64: string }[],
		totalisedReportItems?: TotalisedReportItem[],
		pieCharts?: { nodeUID: string, name: string; base64: string }[]) {
		this.now = new Date();

		let inputData: EnergyReportRequest = {
			AccountGUID: this.accountGUID,
			OrganisationUID: this.organisationUID,
			OrganisationName: this.organisation.Name,
			ReportTitle: `${this.organisation.Name} - ${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)} (${this.getPeriodLength(this.numDays)})`,
			PeriodStartPoint: this.formatDate(this.startDate),
			PeriodEndPoint: this.formatDate(this.endDate),
			PeriodLength: this.getPeriodLength(this.numDays),
			IncludedLocations: this.selectedLocation ? this.selectedLocation.Name : "All Locations",
			TargetSI: this.targetSI,
			SiteCurrent: this.recentRunningValue,
			SitePeriodAvg: this.totalAvgPeriod,
			SiteRunningAvg: this.totalAvgRunning,
			SiteDifference: this.totalDifference,
			SiteTotal: this.totalMax,
			DatapointReportItems: datapointReportItems,
			SiteDeltaChartBase64: deltaBase64,
			SiteTotalChartBase64: totalBase64,
			DatapointGraphBase64s: dpCharts,
			TotalisedReportItems: totalisedReportItems,
			TotalisedBase64s: pieCharts,
			IncludedNodes: this.includedNodes.map((node: OrganisationDataModelNode) => node.DataModelNodeUID)
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.generateSimpleEnergyReportPDF$(this.accountGUID, this.organisationUID, inputData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				// Determine the MIME type and handle accordingly
				const mimeType = response.type || 'application/pdf'; // Default to PDF if type is not provided
				let fileName = `${this.organisation.Name.replaceAll(" ", "_")}_Utility_Report_${this.formatDate(this.now, true)}`;

				if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					fileName += '.xlsx';
				} else if (mimeType === 'application/pdf') {
					fileName += '.pdf';
				} else {
					fileName += '.txt'; // Default to txt for unknown types
				}

				let anchor = document.createElement('a');
				const blob = new Blob([response], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				anchor.download = fileName;
				anchor.href = url;
				anchor.click();

				window.URL.revokeObjectURL(url);
				anchor.remove();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.generateEnergyReportPDF$:", error);
				}
			},
		});
	}

	/**
	 * generateExcelReport
	 */
	generateExcelReport(datapointReportItems: DatapointReportItem[],
		deltaBase64: string,
		totalBase64: string,
		dpCharts: { datapointID: any; dptitle: string; base64: string }[],
		totalisedReportItems?: TotalisedReportItem[],
		pieCharts?: { nodeUID: string, name: string; base64: string }[]) {
		this.now = new Date();

		let inputData: EnergyReportRequest = {
			AccountGUID: this.accountGUID,
			OrganisationUID: this.organisationUID,
			OrganisationName: this.organisation.Name,
			ReportTitle: `${this.organisation.Name} Utility Report ${this.formatDate(this.now)}`,
			PeriodStartPoint: this.formatDate(this.startDate),
			PeriodEndPoint: this.formatDate(this.endDate),
			PeriodLength: this.getPeriodLength(this.numDays),
			IncludedLocations: this.selectedLocation ? this.selectedLocation.Name : "All Locations",
			TargetSI: this.targetSI,
			SiteCurrent: this.recentRunningValue,
			SitePeriodAvg: this.totalAvgPeriod,
			SiteRunningAvg: this.totalAvgRunning,
			SiteDifference: this.totalDifference,
			SiteTotal: this.totalMax,
			DatapointReportItems: datapointReportItems,
			SiteDeltaChartBase64: deltaBase64,
			SiteTotalChartBase64: totalBase64,
			DatapointGraphBase64s: dpCharts,
			TotalisedReportItems: totalisedReportItems,
			TotalisedBase64s: pieCharts,
			IncludedNodes: this.includedNodes.map((node: OrganisationDataModelNode) => node.DataModelNodeUID)
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.generateEnergyReportExcel$(this.accountGUID, this.organisationUID, inputData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				// Determine the MIME type and handle accordingly
				const mimeType = response.type || 'application/pdf'; // Default to PDF if type is not provided
				let fileName = `${this.organisation.Name.replaceAll(" ", "_")}_Utility_Report_${this.formatDate(this.now, true)}`;

				if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					fileName += '.xlsx';
				} else if (mimeType === 'application/pdf') {
					fileName += '.pdf';
				} else {
					fileName += '.txt'; // Default to txt for unknown types
				}

				let anchor = document.createElement('a');
				const blob = new Blob([response], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				anchor.download = fileName;
				anchor.href = url;
				anchor.click();

				window.URL.revokeObjectURL(url);
				anchor.remove();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.generateEnergyReportExcel$:", error);
				}
			},
		});
	}

	/**
	 * generateSimpleExcelReport
	 */
	generateSimpleExcelReport(datapointReportItems: DatapointReportItem[],
		deltaBase64: string,
		totalBase64: string,
		dpCharts: { datapointID: any; dptitle: string; base64: string }[],
		totalisedReportItems?: TotalisedReportItem[],
		pieCharts?: { nodeUID: string, name: string; base64: string }[]) {
		this.now = new Date();

		let inputData: EnergyReportRequest = {
			AccountGUID: this.accountGUID,
			OrganisationUID: this.organisationUID,
			OrganisationName: this.organisation.Name,
			ReportTitle: `${this.organisation.Name} Utility Report ${this.formatDate(this.now)}`,
			PeriodStartPoint: this.formatDate(this.startDate),
			PeriodEndPoint: this.formatDate(this.endDate),
			PeriodLength: this.getPeriodLength(this.numDays),
			IncludedLocations: this.selectedLocation ? this.selectedLocation.Name : "All Locations",
			TargetSI: this.targetSI,
			SiteCurrent: this.recentRunningValue,
			SitePeriodAvg: this.totalAvgPeriod,
			SiteRunningAvg: this.totalAvgRunning,
			SiteDifference: this.totalDifference,
			SiteTotal: this.totalMax,
			DatapointReportItems: datapointReportItems,
			SiteDeltaChartBase64: deltaBase64,
			SiteTotalChartBase64: totalBase64,
			DatapointGraphBase64s: dpCharts,
			TotalisedReportItems: totalisedReportItems,
			TotalisedBase64s: pieCharts,
			IncludedNodes: this.includedNodes.map((node: OrganisationDataModelNode) => node.DataModelNodeUID)
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.generateSimpleEnergyReportExcel$(this.accountGUID, this.organisationUID, inputData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				// Determine the MIME type and handle accordingly
				const mimeType = response.type || 'application/pdf'; // Default to PDF if type is not provided
				let fileName = `${this.organisation.Name.replaceAll(" ", "_")}_Utility_Report_${this.formatDate(this.now, true)}`;

				if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					fileName += '.xlsx';
				} else if (mimeType === 'application/pdf') {
					fileName += '.pdf';
				} else {
					fileName += '.txt'; // Default to txt for unknown types
				}

				let anchor = document.createElement('a');
				const blob = new Blob([response], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				anchor.download = fileName;
				anchor.href = url;
				anchor.click();

				window.URL.revokeObjectURL(url);
				anchor.remove();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.generateEnergyReportExcel$:", error);
				}
			},
		});
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * formatDate
	 * @param date date  
	 * @returns 
	 */
	formatDate(date: Date, fileName: boolean = false): string {
		const pad = (n: number) => n.toString().padStart(2, '0');

		const day = pad(date.getDate());
		const month = pad(date.getMonth() + 1); // Months are 0-indexed
		const year = date.getFullYear(); // Get last 2 digits
		const hours = pad(date.getHours());
		const minutes = pad(date.getMinutes());
		const seconds = pad(date.getSeconds());

		if (fileName) {
			return `${day}-${month}-${year}_${hours}-${minutes}-${seconds}`;
		} else {
			return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
		}
	}

	/**
	 * getPeriodLength
	 * @param numDays numDays 
	 * @returns 
	 */
	getPeriodLength(numDays: number): string {
		let result: string = "";
		result = `${numDays < 1 ? (numDays * 24).toFixed(0) : numDays} ${numDays < 1 ? "Hours" : numDays > 1 ? "Days" : "Day"}`
		return result;
	}

	/**
	 * trendDeltaValues
	 */
	trendDeltaValues() {
		setTimeout(() => {
			if (this.sumDeltaChart) {
				this.sumDeltaChart.addSeries([{
					animation: {
						enable: false
					},
					emptyPointSettings: { mode: "Drop" },
					type: 'Line',
					dataSource: this.totalDeltaData,
					name: "Total Running",
					xName: 'timestamp',
					yName: `Total_DELTA`,
					marker: {
						visible: false,
						width: 8,
						height: 8,
						shape: 'Triangle',
						dataLabel: { "name": 'x' },
						isFilled: true
					},
					// yAxisName: `Axis${this.targetSI}`,
					fill: this.deltaSeriesColour
				}])
			}
		}, 10);
	}

	/**
	 * trendDeltaValues
	 */
	trendTotalValues() {
		setTimeout(() => {
			if (this.sumTotalChart) {
				this.sumTotalChart.primaryYAxis.interval = parseFloat((this.totalDifference / 5).toFixed(0));
				this.sumTotalChart.primaryYAxis.minimum = parseInt((this.totalMin - <number>this.sumTotalChart.primaryYAxis.interval).toFixed(0));
				this.sumTotalChart.primaryYAxis.maximum = parseInt((this.totalMax + <number>this.sumTotalChart.primaryYAxis.interval).toFixed(0));
				this.sumTotalChart.addSeries([{
					animation: {
						enable: false
					},
					emptyPointSettings: { mode: "Drop" },
					type: 'Line',
					dataSource: this.totalCumulativeData,
					name: "Total Cumulative",
					xName: 'timestamp',
					yName: `Total`,
					marker: {
						visible: false,
						width: 8,
						height: 8,
						shape: 'Triangle',
						dataLabel: { "name": 'x' },
						isFilled: true
					},
					// yAxisName: `Axis${this.targetSI}`,
					fill: this.deltaSeriesColour
				}])
			}
		}, 10);
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: "DateTime", //'Category',
			intervalType: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "Days" : "Hours",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "dd-MMM" : "HH:mm dd-MMM",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			// labelRotation: 35,
			// labelRotation: 90,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/**
	 * exportDeltaChart
	 */
	convertChartToBase64(chart: ChartComponent | AccumulationChartComponent): string {
		let result: string = "";
		if (chart) {
			const resultUrl = chart.exportModule.getDataUrl(chart);
			let canvas = resultUrl.element as HTMLCanvasElement;
			const base64 = canvas.toDataURL("image/png");
			result = base64;
		}
		return result;
	}

	/**
	 * getUsageValue
	 * calculates a usage value for a datapoint with cumulative data by using the delta value and delta time value provided in the data
	 * the formula is (factor / delta time) * delta value, where factor is used to convert the value to an hourly usage (3600000 ms in an hour)
	 * @param data data 
	 * @returns 
	 */
	getUsageValue(data: ISensorData, datapointID: number): number | undefined {
		let result = undefined;
		let deltaTimestamp = data[`[${datapointID}_DELTA_TIME]`];
		let deltaValue = data[`[${datapointID}_DELTA]`];
		let factor: number = 60 * 60 * 1000; //hour factor
		if (deltaTimestamp && (deltaValue != undefined)) {
			result = (factor / (<number>deltaTimestamp)) * <number>deltaValue;
		}
		if (result && result < 0) {
			result = 0;
		}
		return result;
	}

	/**
	 * plotRelevantDPData
	 */
	plotRelevantDPData() {
		this.datapointRelevantData.forEach((item) => {
			const chart = this.charts.find((x: ChartComponent) => x.element.id == `chart_${item.DatapointID}`);
			const energyMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == item.DatapointID);
			if (chart) {
				let axis: any = {
					// Assuming you want each axis to have a unique name based on the unit
					name: `Axis${item.SI}`,
					labelFormat: `{value} ${item.SI}`,

				};
				chart.primaryYAxis = axis;
				chart.addSeries([{
					animation: {
						enable: false
					},
					emptyPointSettings: { mode: "Drop" },
					type: 'Line',
					dataSource: item.RelevantDeltaData,
					name: `${item.DPTitle} Running`,
					xName: 'timestamp',
					yName: item.SI != "kWh" ? `[${item.DatapointID}_DELTA]` : `[${item.DatapointID}_DELTA_CALCULATED]`,
					marker: {
						visible: false,
						width: 8,
						height: 8,
						shape: 'Triangle',
						dataLabel: { "name": 'x' },
						isFilled: true
					},
					fill: this.deltaSeriesColour,
					yAxisName: `Axis${item.SI}`
				}]);
				if (energyMetric) {
					const avgLineData: ISensorData[] = [
						{ timestamp: this.startDate.getTime(), sampleDate: "", val: energyMetric.Avg },
						{ timestamp: this.endDate.getTime(), sampleDate: "", val: energyMetric.Avg }
					];
					chart.addSeries([{
						animation: {
							enable: false
						},
						emptyPointSettings: { mode: "Drop" },
						type: 'Line',
						dataSource: avgLineData,
						name: `Average`,
						xName: 'timestamp',
						yName: `val`,
						marker: {
							visible: false,
							width: 8,
							height: 8,
							shape: 'Triangle',
							dataLabel: { "name": 'x' },
							isFilled: true
						},
						fill: "red",
						yAxisName: `Axis${item.SI}`
					}]);
					const runningAvgLineData: ISensorData[] = [
						{ timestamp: this.startDate.getTime(), sampleDate: "", val: energyMetric.RunningAvg },
						{ timestamp: this.endDate.getTime(), sampleDate: "", val: energyMetric.RunningAvg }
					];
					chart.addSeries([{
						animation: {
							enable: false
						},
						emptyPointSettings: { mode: "Drop" },
						type: 'Line',
						dataSource: runningAvgLineData,
						name: `Running Average`,
						xName: 'timestamp',
						yName: `val`,
						marker: {
							visible: false,
							width: 8,
							height: 8,
							shape: 'Triangle',
							dataLabel: { "name": 'x' },
							isFilled: true
						},
						fill: "purple",
						yAxisName: `Axis${item.SI}`
					}]);
				}
			}
		});
	}

	/**
	 * convertAllDatapointChartsToBase64
	 * @returns 
	 */
	convertAllDatapointChartsToBase64(): { datapointID: any; dptitle: string; base64: string }[] {
		let result: { datapointID: any; dptitle: string, base64: string }[] = [];
		this.datapointRelevantData.forEach((item) => {
			const chart = this.charts.find((x: ChartComponent) => x.element.id == `chart_${item.DatapointID}`);
			if (chart) result.push({ datapointID: item.DatapointID, dptitle: item.DPTitle, base64: this.convertChartToBase64(chart) });
		});
		return result;
	}

	/**
	 * convertAllPieChartsToBase64
	 * @returns 
	 */
	async convertAllPieChartsToBase64(): Promise<{ nodeUID: string, name: string; base64: string }[]> {
		return new Promise<{ nodeUID: string, name: string; base64: string }[]>(async (resolve, reject) => {
			let result: { nodeUID: string, name: string; base64: string }[] = [];
			for (let i: number = 0; i < this.pieChartData.length; i++) {
				const item = this.pieChartData[i];
				if (item.length > 0) {
					/* fix hanging on empty pie chart */
					if (item.length == 1 && item[0].usage > 0 || item.length != 1) {
						const chart = this.pieCharts.find((x: AccumulationChartComponent) => x.element.id == `pie_${item[0].nodeUID}`);
						const b64: string = await this.convertPieChartToBase64(item[0].nodeUID);
						if (chart) result.push({ nodeUID: item[0].nodeUID, name: item[0].title, base64: b64 });
					}
				}
			}
			resolve(result);
		});
	}

	/**
	 * convertPieChartToBase64
	 * @param chart chart 
	 * @returns 
	 */
	async convertPieChartToBase64(nodeUID: string): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			try {
				let svg: any = document.querySelector(`#pie_${nodeUID}_svg`);
				if (svg) {
					var canvas = document.createElement("canvas");
					document.body.appendChild(canvas);
					var svgSize = svg.getBoundingClientRect();
					canvas.width = svgSize.width;
					canvas.height = svgSize.height;
					let ctx: any = canvas.getContext("2d");
					var svgData = new XMLSerializer().serializeToString(svg);
					var img = document.createElement("img");
					img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
					img.onload = function () {
						ctx.drawImage(img, 0, 0);
						var imagedata = canvas.toDataURL("image/png");
						canvas.remove();
						resolve(imagedata);
					};
				} else {
					reject();
				}
			} catch (e) {
				console.log(e);
				reject();
			}
		});
	}

	/**
	 * getRuntimeString
	 * @param runtime runtime 
	 * @returns 
	 */
	getRuntimeString(runtime: number): string {
		let result: string = "";
		if (runtime > 3600000) {
			result = `${parseFloat((runtime / 1000 / 60 / 60).toFixed(2))} Hr`;
		} else {
			result = `${parseFloat((runtime / 1000 / 60).toFixed(2))} Min`;
		}
		return result;
	}

	/**
	 * respondToDrag
	 * @param event event  
	 */
	respondToDrag(event: any) {
		if (event.draggedNode) {
			this.includedNodes.push(event.draggedNode);
			if (["folder", "organisation"].includes(event.draggedNode.NodeType)) {
				// add all subchildren 
				// this.includedNodes.push(...event.draggedNode.SubChild);
				// get data for all subchildren
				[event.draggedNode, ...event.draggedNode.SubChild].forEach((node: any) => {
					if (node.NodeType != "datapoint") {
						const dpNodes = this.flattenNodes([node], true).filter((x: any) => x.DataPointID);
						const relevantMetrics: DatapointEnergyMetric[] = [];
						dpNodes.forEach((dpNode: any) => {
							const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == dpNode.DataPointID);
							if (findMetric) {
								relevantMetrics.push(findMetric);
								if (node.NodeType == "sensor") {
									this.reportDatapointEnergyMetrics.push(findMetric);
								}
							}
						});
						this.reportTotalisedEnergyMetrics.push(this.getTotaliseEnergyMetric(node, dpNodes, relevantMetrics));
					} else {
						const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == event.draggedNode.DataPointID);
						if (findMetric) {
							this.reportDatapointEnergyMetrics.push(findMetric);
						}
					}
				});
			}
			else if (event.draggedNode.NodeType == "sensor") {
				// add all datapoints
				// get data for all datapoints 
				const relevantMetrics: DatapointEnergyMetric[] = [];
				event.draggedNode.Datapoints.forEach((dp: any) => {
					const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == dp.DataPointID);
					if (findMetric) {
						relevantMetrics.push(findMetric);
						this.reportDatapointEnergyMetrics.push(findMetric);
					}
					this.reportTotalisedEnergyMetrics.push(this.getTotaliseEnergyMetric(event.draggedNode, event.draggedNode.Datapoints, relevantMetrics));
				});
			}
			else if (event.draggedNode.NodeType == "datapoint") {
				// add to report
				// get data
				const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == event.draggedNode.DataPointID);
				if (findMetric) {
					this.reportDatapointEnergyMetrics.push(findMetric);
				}
			}
			// if (!event.draggedNode.DataPointID) {
			// 	const dpNodes = this.flattenNodes([event.draggedNode], true).filter((x: any) => x.DataPointID);
			// 	const relevantMetrics: DatapointEnergyMetric[] = [];
			// 	dpNodes.forEach((node: any) => {
			// 		const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == node.DataPointID);
			// 		if (findMetric) {
			// 			relevantMetrics.push(findMetric);
			// 			this.reportDatapointEnergyMetrics.push(findMetric);
			// 		}
			// 	});
			// 	this.reportTotalisedEnergyMetrics.push(this.getTotaliseEnergyMetric(event.draggedNode, dpNodes, relevantMetrics));
			// } else {
			// 	const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == event.draggedNode.DataPointID);
			// 	if (findMetric) {
			// 		this.reportDatapointEnergyMetrics.push(findMetric);
			// 	}
			// }
			if (this.listViewInstance) {
				this.listViewInstance.dataBind();
				this.listViewInstance.refresh();
			}
		}
	}

	/**
	 * getNodeChildrenText
	 * @param OrganisationDataModelNode OrganisationDataModelNode 
	 */
	getNodeChildrenText(node: OrganisationDataModelNode): string {
		let result: string = "";
		let count: number | undefined = 0;
		switch (node.NodeType) {
			case "sensor":
				count = node.Datapoints?.length;
				result = `${count} DataPoint${count != 1 ? 's' : ''}`;
				break;
			case "organisation":
				count = node.SubChild?.length;
				result = `${count} ${count != 1 ? 'Children' : 'Child'}`;
				break;
			case "folder":
				count = node.SubChild?.length;
				result = `${count} ${count != 1 ? 'Children' : 'Child'}`;
				break;
			case "datapoint":
				break;
			default:
				break;
		}
		return result;
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * removeReportItemNode
	 * @param node node 
	 */
	removeReportItemNode(node: any) {
		if (node.DataPointID) {
			this.includedNodes = this.includedNodes.filter((x: any) => (x.DataPointID && x.DataPointID != node.DataPointID) || !x.DataPointID);
			this.reportDatapointEnergyMetrics = this.reportDatapointEnergyMetrics.filter((x: DatapointEnergyMetric) => x.DatapointID != node.DataPointID);
		} else {
			this.includedNodes = this.includedNodes.filter((x: any) => (x.DataModelNodeID && x.DataModelNodeID != node.DataModelNodeID) || !x.DataModelNodeID);
			const flattenedNodes = this.flattenNodes([node], true);
			flattenedNodes.forEach((_node: any) => {
				if (_node.DataPointID) {
					const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == _node.DataPointID);
					if (findMetric) {
						this.reportDatapointEnergyMetrics = this.reportDatapointEnergyMetrics.filter((x: DatapointEnergyMetric) => x.DatapointID != _node.DataPointID);
					}
				}
			});
			this.reportTotalisedEnergyMetrics = this.reportTotalisedEnergyMetrics.filter((x: TotalisedEnergyMetric) => x.NodeUID! != node.DataModelNodeUID);
		}
		if (this.listViewInstance) {
			this.listViewInstance.dataBind();
			this.listViewInstance.refresh();
		}
	}

	/**
	 * setDataModel
	 * @param model model  
	 */
	setDataModel(model: OrganisationDataModelNode[]) {
		this.organisationDataModel = model;
		this.flattenedDataModel = this.flattenNodes(this.organisationDataModel, true);
	}

	/**
	 * setPieChartData
	 */
	setPieChartData() {
		if (this.flattenedDataModel.length > 0) {
			this.flattenedDataModel.forEach((node: OrganisationDataModelNode) => {
				if (node.NodeType == "folder") {
					const dpNodes = this.flattenNodes([node], true).filter((x: any) => x.DataPointID);
					const relevantMetrics = this.datapointEnergyMetrics
						.filter((x: DatapointEnergyMetric) => dpNodes.map((dpNode: any) => dpNode.DataPointID).includes(x.DatapointID));
					this.pieChartData.push(this.calculatePieChartData(node, dpNodes, relevantMetrics));
				}
			});
		} else {
			setTimeout(() => {
				this.setPieChartData();
			}, 100);
		}
	}

	/**
	 * openReportConfiguration
	 */
	openReportConfiguration() {

		let data: CreateOrganisationReportData = {
			DataModel: this.organisationDataModel,
			DPEnergyMetrics: this.datapointEnergyMetrics
		};

		const dialogRef = this.dialog.open(
			CreateOrganisationReportComponent,
			{
				data: data
			}
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe((result: any) => {
			let newReport: SummaryReport = this.createSummaryReportFromDialogResult(result);
			this.postSummaryReport(newReport);
		});
	}

	/**
	 * postSummaryReport
	 * @param newReport newReport 
	 */
	postSummaryReport(newReport: SummaryReport) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.postOrganisationSummaryReport$(this.accountGUID, this.organisationUID, newReport).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: "Report Created",
					duration: 2000,
					action: ""
				});
				this.getSummaryReports();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.postOrganisationSummaryReport$:", error);
				}
			},
		});
	}

	/**
	 * getSummaryReports
	 */
	getSummaryReports() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.getOrganisationSummaryReports$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.summaryReports = response;
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.getOrganisationSummaryReports$:", error);
				}
			},
		});
	}


	/**
	 * getTotaliseEnergyMetric
	 * @param node node 
	 */
	getTotaliseEnergyMetric(node: OrganisationDataModelNode, datapoints: any[], metrics: DatapointEnergyMetric[]): TotalisedEnergyMetric {
		const totalDiff = metrics.map((x: DatapointEnergyMetric) => x.Diff).reduce((sum, val) => sum + val, 0);
		const totalAvg = metrics.map((x: DatapointEnergyMetric) => x.Avg).reduce((sum, val) => sum + val, 0);
		const totalRunningAvg = metrics.map((x: DatapointEnergyMetric) => x.RunningAvg).reduce((sum, val) => sum + val, 0);
		const totalMax = metrics.map((x: DatapointEnergyMetric) => x.Max).reduce((sum, val) => sum + val, 0);
		const totalMin = metrics.map((x: DatapointEnergyMetric) => x.Min).reduce((sum, val) => sum + val, 0);
		const flattenedNodes = this.flattenNodes([node], true);
		const dpIDs: number[] = [...new Set(flattenedNodes.map((x: any) => x.DataPointID).filter((x) => x != undefined))];
		let units: string = "";
		if ([...new Set(datapoints.map((x: DataPoint) => x.SI))].length == 1) {
			units = datapoints[0].SI;
		}
		let result: TotalisedEnergyMetric = {
			Name: node.Name,
			Avg: parseFloat(totalAvg.toFixed(3)),
			RunningAvg: parseFloat(totalRunningAvg.toFixed(3)),
			Total: 0,
			Diff: parseFloat(totalDiff.toFixed(3)),
			Min: parseFloat(totalMin.toFixed(3)),
			Max: parseFloat(totalMax.toFixed(3)),
			RunTime: 0,
			NodeUID: node.DataModelNodeUID,
			DataPointIDs: dpIDs,
			ParentUID: node.ParentNodeUID ?? "",
			NodeType: node.NodeType ?? "",
			Units: units
		};
		return result;
	}

	/**
	 * editSummaryReport
	 * @param summaryReport summaryReport 
	 */
	editSummaryReport(summaryReport: SummaryReport) {
		let data: CreateOrganisationReportData = {
			DataModel: this.organisationDataModel,
			DPEnergyMetrics: this.datapointEnergyMetrics,
			ExistingReport: summaryReport
		};

		const dialogRef = this.dialog.open(
			CreateOrganisationReportComponent,
			{
				data: data
			}
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe((result: any) => {
			let report: SummaryReport = this.createSummaryReportFromDialogResult(result);
			this.patchSummaryReport(summaryReport.OrganisationReportID!, report);
		});
	}

	/**
	 * createSummaryReportFromDialogResult
	 * @param result result 
	 * @returns 
	 */
	createSummaryReportFromDialogResult(result: any): SummaryReport {
		let newReport: SummaryReport = {
			Name: result.name,
			Description: result.description,
			FileType: result.fileType,
			Interval: result.interval,
			FolderNodes: result.includedNodes
				.filter((x: OrganisationDataModelNode) => x.NodeType == 'folder' || x.NodeType == 'organisation')
				.map((x: OrganisationDataModelNode) => x.DataModelNodeUID).toString(),
			SensorNodes: result.includedNodes
				.filter((x: OrganisationDataModelNode) => x.NodeType == 'sensor')
				.map((x: OrganisationDataModelNode) => x.DataModelNodeUID).toString(),
			DatapointNodes: result.includedNodes
				.filter((x: any) => x.DataPointID)
				.map((x: DataPoint) => x.DataPointID).toString()
		};

		return newReport;
	}

	/**
	 * patchSummaryReport
	 * @param report report 
	 */
	patchSummaryReport(reportID: number, report: SummaryReport) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.patchOrganisationSummaryReport$(this.accountGUID, this.organisationUID, reportID, report).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: "Report Updated",
					duration: 2000,
					action: ""
				});
				this.getSummaryReports();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.patchOrganisationSummaryReport$:", error);
				}
			},
		});
	}

	/**
	 * checkUniqueID
	 * @param dpIDs dpIDs 
	 * @param checkingID checkingID 
	 * @param checkingIndex checkingIndex 
	 * @returns 
	 */
	checkUniqueID(dpIDs: number[], checkingID: number, checkingIndex: number): boolean {
		return dpIDs.indexOf(checkingID) === checkingIndex;
	}

	/**
	 * deleteSummaryReport
	 * @param summaryReport summaryReport 
	 */
	requestDeleteSummaryReport(summaryReport: SummaryReport) {
		this.globalFunctions.UserConfirm$(`Delete Summary Report - ${summaryReport.Name}?`).subscribe(OKResult => {
			if (OKResult) {
				this.deleteSummaryReport(summaryReport.OrganisationReportID!);
			}
		});
	}

	/**
	 * deleteSummaryReport
	 * @param reportID reportID 
	 */
	deleteSummaryReport(reportID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.reportPDFService.deleteOrganisationSummaryReport$(this.accountGUID, this.organisationUID, reportID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: "Report Deleted",
					duration: 2000,
					action: ""
				});
				this.getSummaryReports();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportPDFService.deleteOrganisationSummaryReport$:", error);
				}
			},
		});
	}

	/**
	 * calculatePieChartData
	 * @param draggedNode draggedNode 
	 * @param datapointNodes datapointNodes 
	 * @param datapointMetrics datapointMetrics 
	 * @returns 
	 */
	calculatePieChartData(draggedNode: OrganisationDataModelNode, datapointNodes: any[], datapointMetrics: DatapointEnergyMetric[]): { title: string, nodeUID: string, name: string, usage: number }[] {
		const result: { title: string, nodeUID: string, name: string, usage: number }[] = [];
		datapointMetrics.forEach((metric: DatapointEnergyMetric) => {
			const dp = datapointNodes.find((x: any) => x.DataPointID == metric.DatapointID);
			result.push({
				title: draggedNode.Name,
				nodeUID: draggedNode.DataModelNodeUID,
				name: `${dp.SensorName}: ${metric.Diff}${dp.SI}`,
				usage: metric.Diff
			});
		});
		return result;
	}

	/**
	 * goToReportHistory
	 */
	goToReportHistory() {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/utility-report-history`]);
	}
}
